import { useEffect, useState } from "react";
import axios from "axios";
import { HELP_CONTENT_BASE_URL, PaymentsHelpContentKey } from "../constants";

/** Cached across mounts — this copy changes rarely and is the same for everyone. */
const cache = new Map<string, string>();

/**
 * Loads a help document from the CDN.
 *
 * The response is `{ text: "<html>" }`. The HTML is authored outside the app,
 * so callers must sanitise it before rendering — see HelpContent.
 */
export default function useHelpContent(linkName: PaymentsHelpContentKey) {
  const [html, setHtml] = useState<string | null>(
    () => cache.get(linkName) ?? null
  );
  const [loading, setLoading] = useState(!cache.has(linkName));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const cached = cache.get(linkName);
    if (cached !== undefined) {
      setHtml(cached);
      setLoading(false);
      return;
    }

    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await axios.get(
          `${HELP_CONTENT_BASE_URL}/${linkName}.json`
        );
        const text = data?.text ?? "";
        cache.set(linkName, text);
        if (active) setHtml(text);
      } catch (err) {
        if (active) setError(err as Error);
      } finally {
        if (active) setLoading(false);
      }
    })();

    // The screen may unmount while the request is in flight.
    return () => {
      active = false;
    };
  }, [linkName]);

  return { html, loading, error };
}
